import { useQuery } from '@tanstack/react-query';
import { Command } from 'cmdk';
import * as Dialog from '@radix-ui/react-dialog';
import { useEffect, useRef, useState, type ReactNode } from 'react';

import { Chip } from '@/components/ui/Chip';
import { symbolSearchQuery, type SymbolHitOut } from '@/lib/api/queries';
import { directionClass, formatPercent } from '@/lib/market/formatters';
import { formatPrice } from '@/lib/registry/format';
import type { Market } from '@/lib/market/types';

/**
 * =============================================================================
 *  SymbolPicker —— ARCHITECTURE §10.5.2：`cmdk` combobox，比對四種輸入：
 *  代號前綴、中文名子字串、英文名、**注音／拼音首字母**（`tjd` → 台積電）。
 *
 *  兩個容易做錯的地方：
 *
 *  1. **`shouldFilter={false}`。** cmdk 預設會在瀏覽器端再過濾一次它拿到的清單。
 *     那會把後端好不容易用 GIN trgm + 拼音首字母找出來的結果再砍一次——
 *     打 `tjd` 時後端回了台積電，cmdk 發現「台積電」裡沒有 `tjd` 這三個字元，
 *     於是把它濾掉，畫面顯示「找不到」。比對邏輯的權威在後端，這裡只負責顯示。
 *
 *  2. **選完不 remount。** `onSelect` 只寫 store 與 URL（§10.2 規則 3）。
 *     這個元件不持有 symbol，所以它換股票時不會把整頁拖著一起重建。
 * ========================================================================== */

export interface SymbolPickerProps {
  market: Market;
  symbol: string;
  /** 目前這一檔的中文名，用來畫按鈕。搜尋結果回來前先顯示代號。 */
  name?: string | undefined;
  asOf: string | undefined;
  onSelect: (hit: { symbol: string; market: Market }) => void;
  /** 自訂觸發元件（頂部搜尋列用）。有給時不畫預設的「切換股票」按鈕，並啟用 ⌘K／Ctrl+K 開啟。 */
  trigger?: ((open: () => void) => ReactNode) | undefined;
}

const MARKET_LABEL: Record<Market, string> = { tw: '台股', us: '美股' };

/** 後端把命中的原因分成五類；把它顯示出來，使用者才知道自己為什麼會搜到這一檔。 */
const MATCH_LABEL: Record<SymbolHitOut['match'], string> = {
  symbol_exact: '代號',
  symbol_prefix: '代號前綴',
  phonetic: '拼音／注音',
  name: '名稱',
  other: '其他',
};

function MatchHint({ match }: { match: SymbolHitOut['match'] }) {
  return <span className="lbl shrink-0">{MATCH_LABEL[match]}</span>;
}

export function SymbolPicker({ market, symbol, name, asOf, onSelect, trigger }: SymbolPickerProps) {
  const [open, setOpen] = useState(false);
  const [raw, setRaw] = useState('');
  const [q, setQ] = useState('');
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  // 150ms debounce：比 hover 預取的 120ms 略長，因為打字比滑鼠移動慢，
  // 而且每一次擊鍵都送一次查詢會讓 11,000 列的 trgm 索引做白工。
  useEffect(() => {
    if (timer.current !== undefined) clearTimeout(timer.current);
    timer.current = setTimeout(() => {
      setQ(raw.trim());
    }, 150);
    return () => {
      if (timer.current !== undefined) clearTimeout(timer.current);
    };
  }, [raw]);

  const hasCustomTrigger = trigger !== undefined;
  useEffect(() => {
    if (!hasCustomTrigger) return;
    const onKey = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
        event.preventDefault();
        setOpen(true);
      }
    };
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('keydown', onKey);
    };
  }, [hasCustomTrigger]);

  const enabled = open && q.length > 0;
  // **搜尋是分市場的**：端點的兩條索引第一欄都是 market，拿掉它會讓
  // 兩條路徑同時退化成全表掃描（見 queries.ts 的註解）。所以這裡搜目前的市場，
  // 想找另一個市場的標的就先切市場藥丸——那比讓每一次擊鍵都掃全表便宜得多。
  const search = useQuery({
    ...symbolSearchQuery(market, q, asOf, 12),
    enabled,
  });

  const items = search.data?.items ?? [];

  return (
    <>
      {trigger !== undefined ? (
        trigger(() => {
          setOpen(true);
        })
      ) : (
      <button
        type="button"
        onClick={() => {
          setOpen(true);
        }}
        className="flex min-w-0 items-baseline gap-2 glass rounded-control px-2 py-1 text-left"
        aria-label="切換股票"
      >
        <span className="truncate text-16 font-semibold text-fg-1">{name ?? symbol}</span>
        <span className="mono text-13 text-fg-2">{symbol}</span>
        <span className="lbl shrink-0" aria-hidden="true">
          切換
        </span>
      </button>
      )}

      <Dialog.Root open={open} onOpenChange={setOpen}>
        <Dialog.Portal>
          <Dialog.Overlay className="sheet-overlay fixed inset-0 z-40 bg-scrim" />
          <Dialog.Content
            className="fixed top-[max(0.75rem,var(--safe-top))] left-1/2 z-50 w-[min(560px,calc(100%-1.5rem))] -translate-x-1/2 overflow-hidden rounded-card border border-border-1 bg-surface-1 shadow-none outline-none sm:top-[12vh] sm:w-[min(560px,94vw)]"
            aria-label="搜尋股票"
          >
            <Dialog.Title className="sr-only">搜尋股票</Dialog.Title>
            <Dialog.Description className="sr-only">
              輸入代號、中文名、英文名，或拼音／注音首字母。
            </Dialog.Description>
            <Command shouldFilter={false} loop>
              <div className="flex items-center gap-2 border-b border-border-1 px-3 py-3 sm:py-2">
                <Command.Input
                  autoFocus
                  value={raw}
                  onValueChange={setRaw}
                  placeholder="代號、名稱，或拼音首字母（tjd）"
                  className="w-full bg-transparent text-14 text-fg-1 outline-none placeholder:text-muted"
                />
                {search.isFetching && <span className="lbl shrink-0">搜尋中</span>}
              </div>

              <Command.List className="max-h-[42dvh] overflow-y-auto overscroll-contain sm:max-h-[52vh]">
                {q === '' ? (
                  <div className="lbl px-3 py-6 text-center">
                    四種找法都通：代號前綴、中文名、英文名、拼音／注音首字母。
                  </div>
                ) : search.isError ? (
                  <div className="px-3 py-4 text-13 text-fg-2">
                    搜尋服務沒有回應。
                    <button
                      type="button"
                      onClick={() => void search.refetch()}
                      className="ml-2 rounded-control border border-border-2 px-1.5 py-0.5 text-11 hover:bg-surface-2"
                    >
                      重試
                    </button>
                  </div>
                ) : (
                  <>
                    <Command.Empty className="lbl px-3 py-6 text-center">
                      {search.isFetching ? '搜尋中' : `找不到符合「${q}」的標的`}
                    </Command.Empty>
                    {items.map((hit) => (
                      <Command.Item
                        key={`${hit.market}:${hit.symbol}`}
                        value={`${hit.market}:${hit.symbol}`}
                        onSelect={() => {
                          onSelect({ symbol: hit.symbol, market: hit.market });
                          setOpen(false);
                          setRaw('');
                        }}
                        className="flex cursor-pointer items-baseline gap-2 border-b border-border-1 px-3 py-2.5 last:border-b-0 data-[selected=true]:bg-surface-2 sm:py-1.5"
                      >
                        <span className="mono w-14 shrink-0 text-13 font-semibold text-fg-1 sm:w-16">
                          {hit.symbol}
                        </span>
                        <span className="min-w-0 flex-1 truncate text-13 text-fg-1">
                          {hit.name ?? hit.symbol}
                          {hit.name_en == null ? null : (
                            <span className="lbl ml-1">{hit.name_en}</span>
                          )}
                        </span>
                        <MatchHint match={hit.match} />
                        {hit.is_delisted && <Chip tone="warn">已下市</Chip>}
                        {hit.market === market ? null : (
                          <Chip tone="neutral">{MARKET_LABEL[hit.market]}</Chip>
                        )}
                        <span className="num w-16 shrink-0 text-right text-13 text-fg-2">
                          {formatPrice(hit.last_close)}
                        </span>
                        <span
                          className={`num w-14 shrink-0 text-right text-11 ${directionClass(hit.chg_pct ?? 0)}`}
                        >
                          {hit.chg_pct == null ? '—' : formatPercent(hit.chg_pct)}
                        </span>
                      </Command.Item>
                    ))}
                  </>
                )}
              </Command.List>
            </Command>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>
    </>
  );
}
